import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Form,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { env } from "@/env";
import { StorageUtility } from "@/lib/storage";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { datadogRum } from "@datadog/browser-rum";
import { COMMON_FETCH_HEADERS } from "@/lib/utils";
import { z } from "zod/v4";
import { parseVolumeJwt, isSafeInternalUrl } from "@/lib/auth";
import { zodValidator } from "@tanstack/zod-adapter";
import posthog from "posthog-js";

export const Route = createFileRoute("/auth/_auth-layout/signin")({
  component: () => <SignInComponent />,
  validateSearch: zodValidator(
    z.object({
      afterSignInUrl: z.string().optional(),
    }),
  ),
});

const SignInFormSchema = z.object({
  email: z.string().nonempty({ message: "Email address is required" }),
  password: z.string().nonempty({ message: "Password is required" }),
});

const AuthenticateResponseSchema = z.discriminatedUnion("authenticationState", [
  z.object({
    authenticationState: z.literal("MFA_DISABLED"),
    jwt: z.string(),
  }),
  z.object({
    authenticationState: z.literal("PHONE_SET"),
    otpRequestId: z.string(),
    expiresAt: z.string(),
  }),
  z.object({
    authenticationState: z.literal("NO_PHONE_SET"),
    twoFactorRequestId: z.string(),
    expiresAt: z.string(),
  }),
]);

type SignInFormData = z.infer<typeof SignInFormSchema>;

function SignInComponent() {
  const form = useForm({
    resolver: zodResolver(SignInFormSchema),
    defaultValues: { email: "", password: "" },
  });
  const navigate = useNavigate();
  const { afterSignInUrl } = Route.useSearch();

  const authenticateMutation = useMutation({
    mutationKey: ["authenticate"],
    mutationFn: async (data: SignInFormData) => {
      const response = await fetch(
        `${env.VITE_VOLUME_URL}/api/authenticate-2fa`,
        {
          method: "POST",
          headers: {
            Authorization:
              "Basic " + btoa(data.email.toLowerCase() + ":" + data.password),
            ...COMMON_FETCH_HEADERS,
          },
        },
      );

      if (response.status === 401 || response.status === 403) {
        const message = "Incorrect email or password";
        form.setError("email", { message, type: "validate" });
        form.setError("password", { message, type: "validate" });
        return;
      }

      if (!response.ok) {
        const message = "Authentication failed";
        form.setError("email", { message, type: "validate" });
        form.setError("password", { message, type: "validate" });
        return;
      }

      const responseJson = await response.json();
      const parsedResponse = AuthenticateResponseSchema.parse(responseJson);

      switch (parsedResponse.authenticationState) {
        case "MFA_DISABLED": {
          const accessToken = parsedResponse.jwt;
          const parsed = parseVolumeJwt(accessToken);
          StorageUtility.setItem("v_accessToken_v1", accessToken);

          if (parsed.adminId) {
            StorageUtility.setItem("v_adminEmail_v1", data.email);
            datadogRum.setGlobalContextProperty("adminEmail", data.email);
            posthog.identify(parsed.adminId, { email: data.email });
            navigate({
              to: isSafeInternalUrl(afterSignInUrl) ? afterSignInUrl : "/admin",
            });
            return;
          }

          if (parsed.merchantId) {
            StorageUtility.setItem("v_email_v1", data.email);
            posthog.identify(parsed.merchantId, { email: data.email });
            navigate({
              to: isSafeInternalUrl(afterSignInUrl)
                ? afterSignInUrl
                : "/payments/payin",
            });
            return;
          }

          throw new Error("Invalid user type");
        }
        case "PHONE_SET":
          navigate({
            to: `/auth/verify-otp/${parsedResponse.otpRequestId}`,
            search: true,
          });
          return;
        case "NO_PHONE_SET":
          navigate({
            to: `/auth/set-two-factor/${parsedResponse.twoFactorRequestId}`,
            search: true,
          });
          return;
      }
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : "An error occurred signing in, please try again";

      form.setError("email", { message, type: "validate" });
      form.setError("password", { message, type: "validate" });
    },
  });

  const onSubmit = async (data: SignInFormData) => {
    await authenticateMutation.mutateAsync(data);
  };

  return (
    <div className="flex w-full flex-col  items-center rounded-3xl bg-white text-left">
      <img
        src="/volume-logo.svg"
        className="self-center"
        width={200}
        height={200}
        alt="Volume logo"
      />
      <h1 className="mt-8 px-8 text-center text-3xl font-extrabold">
        Sign in to your account
      </h1>
      <Form {...form}>
        <form
          className="mt-5 flex w-full flex-col items-center"
          onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}
        >
          <div className="flex w-full max-w-xl flex-col items-center px-8 text-left">
            <div className="grid w-full items-center gap-1.5">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">
                      Email Address
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="Enter your email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mt-1 grid w-full items-center gap-1.5">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Enter your Password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mb-6 mt-2 flex w-full flex-row">
              <Link
                to="/auth/forgot-password"
                className="ml-auto cursor-pointer font-semibold text-volume-text-purple"
              >
                Forgot password?
              </Link>
            </div>
            <Button className="w-full p-6 text-lg" type="submit">
              {authenticateMutation.isPending ? (
                <Spinner height={48} width={48} white />
              ) : (
                <>Sign In</>
              )}
            </Button>
            {/**
             * TODO: Once we want merchant self sign up, remove this conditional*/}
            {env.VITE_VOLUME_URL !== "https://api.volumepay.io" ? (
              <div className="m-10 self-center">
                No account yet?{" "}
                <Link to="/auth/signup" className="text-[#6829C5]">
                  Sign up
                </Link>
              </div>
            ) : null}
            {env.VITE_VOLUME_URL === "https://api.volumepay.io" ? (
              <div className="m-10 self-center text-center">
                You should start with a sandbox account before going live with
                Volume.
                <br />
                <a
                  href="https://sandbox.dashboard.volumepay.io/auth/signup"
                  className="text-[#6829C5]"
                >
                  Create a sandbox account
                </a>
              </div>
            ) : null}
          </div>
        </form>
      </Form>
    </div>
  );
}
